// Docs (`/c/{slug}/work/docs?view=`; DOCS-KB design note §2 "Docs"): every doc the caller may
// view across the spaces, one 36 px row each (kind icon, title, space, owner, "edited 2h ago"),
// with the actions on the row: Open, Star, Move, Archive (Restore in Archived), Delete and `…`
// (the same plus Copy link). View tabs filter by kind, starred, recent and archived; the toolbar
// filters by title, space and owner and sorts. `n` opens New doc; `j` / `k` / `Enter` walk rows.
//
// Delete is offered on every row: `PageSummary` carries no `can_edit`, so the server decides and a
// 403 says "Only editors can delete" (the page header uses `can_edit`).
//
// Calls: `pages.list` company-wide with the archived (cursor-followed, `pagesAllQuery`), the
// sidebar's `spaces.list` and `pages.list` (cached), and the seat people for owner names.
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AvatarTile,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Kbd,
  ListRow,
  LocationHeader,
  NativeSelect,
  NativeSelectOption,
  SearchField,
  Skeleton,
  Toolbar,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  ViewTabs,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ArchiveIcon,
  ArchiveRestoreIcon,
  BookOpenIcon,
  EllipsisIcon,
  ExternalLinkIcon,
  FolderInputIcon,
  LinkIcon,
  type LucideIcon,
  PlusIcon,
  SearchIcon,
  Share2Icon,
  StarIcon,
  Trash2Icon,
  UploadIcon,
  WaypointsIcon,
} from "lucide-react";
import { type ReactElement, useMemo, useRef, useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import { LoadError } from "../screen-parts";
import { useFavorites } from "./arrangement";
import { DeletedDocs } from "./deleted-docs";
import { DocsGraph } from "./doc-graph";
import { ago, docFilterOf, docIcon } from "./doc-kinds";
import { AboutDocs } from "./doc-related";
import { DocUploadChip, DropOverlay, UploadDocDialog, uploadKind, useFileDrop } from "./doc-upload";
import { copyDocLink, useDocWrites } from "./doc-writes";
import { useCreateKey, useListKeys } from "./list-keys";
import { useSeatPeople } from "./model";
import {
  MovePageDialog,
  NewDocDialog,
  type NewDocTarget,
  PageSearchDialog,
  SharePageDialog,
} from "./page-dialogs";
import { SharedMarker } from "./space-shares";
import type { PageRow } from "./spaces-tree";
import {
  pageSharesQuery,
  pagesAllQuery,
  pagesQuery,
  spaceDocsMemoryQuery,
  spacesQuery,
} from "./work-queries";
import { BODY } from "./work-screens";

export const DOCS_VIEWS = [
  "all",
  "sop",
  "policy",
  "kb",
  "pages",
  "starred",
  "recent",
  "archived",
] as const;
export type DocsView = (typeof DOCS_VIEWS)[number];

const VIEW_LABELS: Record<DocsView, string> = {
  all: "All",
  sop: "SOPs",
  policy: "Policies",
  kb: "Knowledge base",
  pages: "Pages",
  starred: "Starred",
  recent: "Recent",
  archived: "Archived",
};

type Sort = "edited" | "title" | "created";
const MONTH_MS = 30 * 86_400_000;

export function DocsScreen({
  api,
  company,
  view,
  spaceId: initialSpaceId,
  onView,
}: {
  api: Api;
  company: CompanyAccess;
  view: DocsView;
  /** WORK-OS: the space the screen opens filtered to (`?space=`; the sidebar's Docs rows). */
  spaceId?: string | undefined;
  onView: (view: DocsView) => void;
}) {
  const co = company.id;
  const navigate = useNavigate();
  const docs = useQuery(pagesAllQuery(api, co));
  const spaces = useQuery(spacesQuery(api, co));
  const tree = useQuery(pagesQuery(api, co));
  const people = useSeatPeople(api, co);
  const favorites = useFavorites(co);
  const writes = useDocWrites(api, company);
  const [title, setTitle] = useState("");
  const [spaceId, setSpaceId] = useState(initialSpaceId ?? "");
  const [owner, setOwner] = useState("");
  // RECALL-KB-LINKS: a space's "About these docs" and its tags (memory's, per space); none = no filter
  const [tag, setTag] = useState("");
  const about = useQuery({
    ...spaceDocsMemoryQuery(api, co, spaceId || "none"),
    enabled: spaceId !== "",
  }).data;
  const tagged = about?.tags.find((t) => t.tag === tag)?.page_ids;
  const [sort, setSort] = useState<Sort>("edited");
  const [newDoc, setNewDoc] = useState<NewDocTarget | null>(null);
  const [searching, setSearching] = useState(false);
  // WIKI-LINKS-GRAPH: the docs' link graph in place of the list
  const [graphOpen, setGraphOpen] = useState(false);
  const [moving, setMoving] = useState<PageRow | null>(null);
  // MULTI-SPACE-ACCESS: the Share dialog, and the docs shared into a space (its filter keeps them).
  const [sharing, setSharing] = useState<PageRow | null>(null);
  const shares = useQuery(pageSharesQuery(api, co));
  const sharedInto = (p: PageRow, sid: string) =>
    (shares.data?.data ?? []).some((s) => s.object_id === p.id && s.space_id === sid);
  const [deleting, setDeleting] = useState<PageRow | null>(null);
  const [uploading, setUploading] = useState(false);
  // KB-UPLOAD-2: files dropped anywhere on Docs open the upload dialog with them.
  const [dropped, setDropped] = useState<File[] | undefined>(undefined);
  const drop = useFileDrop((files) => {
    setDropped(files);
    setUploading(true);
  });
  useCreateKey(() => setNewDoc({ spaceId: spaceId || null, parentPageId: null }), true, "n");

  const root = useRef<HTMLUListElement>(null);
  const keys = useMemo(
    () => ({
      onOpen: (row: HTMLElement) => {
        const id = row.dataset.docId;
        if (id)
          void navigate({
            to: "/c/$slug/work/p/$pageId",
            params: { slug: company.slug, pageId: id },
          });
      },
    }),
    [navigate, company.slug],
  );
  useListKeys(root, keys);

  const all = docs.data ?? [];
  const spaceName = (id: string) => spaces.data?.data.find((s) => s.id === id)?.name ?? "";
  const live = all.filter((p) => !p.archived_at);
  const starred = (id: string) => favorites.has({ kind: "page", id });
  const now = Date.now();
  const inView = (p: PageRow) => {
    switch (view) {
      case "archived":
        return Boolean(p.archived_at);
      case "starred":
        return !p.archived_at && starred(p.id);
      case "recent":
        return !p.archived_at && now - new Date(p.updated_at).getTime() <= MONTH_MS;
      case "pages":
        return !p.archived_at && docFilterOf(p.kind) === "page";
      case "sop":
      case "policy":
      case "kb":
        return !p.archived_at && p.kind === view;
      default:
        return !p.archived_at;
    }
  };
  const q = title.trim().toLowerCase();
  const owners = [
    ...new Set(all.map((p) => p.created_by_membership_id).filter((m): m is string => !!m)),
  ]
    .map((m) => ({ id: m, ...people.nameOf(m) }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const rows = all
    .filter(inView)
    .filter((p) => !q || p.title.toLowerCase().includes(q))
    .filter((p) => !spaceId || p.space_id === spaceId || sharedInto(p, spaceId))
    .filter((p) => !owner || p.created_by_membership_id === owner)
    .filter((p) => !tag || !tagged || tagged.includes(p.id))
    .sort((a, b) =>
      sort === "title" && view !== "recent"
        ? a.title.localeCompare(b.title)
        : sort === "created" && view !== "recent"
          ? b.created_at.localeCompare(a.created_at)
          : b.updated_at.localeCompare(a.updated_at),
    );

  const action = (label: string, icon: LucideIcon, props: Record<string, unknown>) => {
    const Icon = icon;
    return (
      <Tooltip>
        <TooltipTrigger
          render={
            <Button variant="ghost" size="icon-sm" aria-label={label} {...(props as object)} />
          }
        >
          <Icon aria-hidden="true" />
        </TooltipTrigger>
        <TooltipContent>{label.split(" ")[0]}</TooltipContent>
      </Tooltip>
    );
  };
  const pageLink = (p: PageRow, extra?: Record<string, unknown>): ReactElement => (
    <Link
      to="/c/$slug/work/p/$pageId"
      params={{ slug: company.slug, pageId: p.id }}
      search={keepFocus}
      {...extra}
    />
  );

  return (
    <div
      data-screen="docs"
      className="relative flex min-h-full min-w-0 flex-col"
      data-dragging={drop.dragging ? "" : undefined}
      {...drop.handlers}
    >
      {drop.dragging ? <DropOverlay /> : null}
      <LocationHeader
        icon={BookOpenIcon}
        title="Docs"
        {...(docs.data ? { count: live.length } : {})}
        actions={
          <>
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Search docs"
                    onClick={() => setSearching(true)}
                  />
                }
              >
                <SearchIcon aria-hidden="true" />
              </TooltipTrigger>
              <TooltipContent>Search titles and text</TooltipContent>
            </Tooltip>
            <Button
              variant={graphOpen ? "secondary" : "ghost"}
              size="sm"
              aria-pressed={graphOpen}
              onClick={() => setGraphOpen((o) => !o)}
            >
              <WaypointsIcon aria-hidden="true" />
              Graph
            </Button>
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="outline"
                    size="sm"
                    aria-label="Upload a document"
                    onClick={() => setUploading(true)}
                  />
                }
              >
                <UploadIcon aria-hidden="true" />
                Upload
              </TooltipTrigger>
              <TooltipContent>
                PDF, Word or text, as Reference, SOP or Page (or drop files)
              </TooltipContent>
            </Tooltip>
            <Button
              size="sm"
              aria-keyshortcuts="n"
              onClick={() => setNewDoc({ spaceId: spaceId || null, parentPageId: null })}
            >
              <PlusIcon aria-hidden="true" />
              New doc
              <Kbd aria-hidden="true" className="ml-1 max-md:hidden">
                n
              </Kbd>
            </Button>
          </>
        }
      />
      {graphOpen ? (
        <div className={BODY}>
          <DocsGraph
            api={api}
            company={company}
            spaces={spaces.data?.data ?? []}
            initialSpaceId={spaceId || undefined}
            onClose={() => setGraphOpen(false)}
          />
        </div>
      ) : null}
      {/* WIKI-LINKS-GRAPH: the list stays mounted (its filters and scroll) behind the graph */}
      <div className={BODY} hidden={graphOpen}>
        <div className="border-b border-border">
          <ViewTabs
            aria-label="Docs views"
            value={view}
            onValueChange={(v) => onView(v as DocsView)}
            tabs={DOCS_VIEWS.map((v) => ({ value: v, label: VIEW_LABELS[v] }))}
          />
        </div>
        <Toolbar aria-label="Docs tools">
          <SearchField
            value={title}
            placeholder="Filter by title…"
            aria-label="Filter by title"
            onChange={(e) => setTitle(e.target.value)}
          />
          <NativeSelect
            size="sm"
            aria-label="Space"
            className="w-40 shrink-0"
            value={spaceId}
            onChange={(e) => {
              setSpaceId(e.target.value);
              setTag("");
            }}
          >
            <NativeSelectOption value="">All spaces</NativeSelectOption>
            {(spaces.data?.data ?? []).map((s) => (
              <NativeSelectOption key={s.id} value={s.id}>
                {s.name}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          <NativeSelect
            size="sm"
            aria-label="Owner"
            className="w-40 shrink-0"
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
          >
            <NativeSelectOption value="">Any owner</NativeSelectOption>
            {owners.map((o) => (
              <NativeSelectOption key={o.id} value={o.id}>
                {o.name}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          {about?.tags.length ? (
            <NativeSelect
              size="sm"
              aria-label="Tag"
              className="w-36 shrink-0"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
            >
              <NativeSelectOption value="">Any tag</NativeSelectOption>
              {about.tags.map((t) => (
                <NativeSelectOption key={t.tag} value={t.tag}>
                  #{t.tag}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          ) : null}
          <span className="shrink-0 text-xs text-muted-foreground tabular-nums" aria-live="polite">
            {docs.data ? `${rows.length} ${rows.length === 1 ? "doc" : "docs"}` : ""}
            {tag && about?.tags.find((t) => t.tag === tag)?.more ? " (possibly more)" : ""}
          </span>
          <NativeSelect
            size="sm"
            aria-label="Sort"
            className="ml-auto w-40 shrink-0"
            value={sort}
            disabled={view === "recent"}
            onChange={(e) => setSort(e.target.value as Sort)}
          >
            <NativeSelectOption value="edited">Recently edited</NativeSelectOption>
            <NativeSelectOption value="title">Title</NativeSelectOption>
            <NativeSelectOption value="created">Created</NativeSelectOption>
          </NativeSelect>
        </Toolbar>
        {about ? <AboutDocs about={about} /> : null}
        {docs.isPending ? (
          <div role="status" aria-label="Loading the docs" className="mt-2 flex flex-col gap-2">
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
          </div>
        ) : docs.isError ? (
          <LoadError what="The docs" error={docs.error} />
        ) : !all.length ? (
          <p className="py-4 text-sm text-muted-foreground">
            No docs yet.{" "}
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0"
              onClick={() => setNewDoc({ spaceId: null, parentPageId: null })}
            >
              New doc
            </Button>{" "}
            starts one.
          </p>
        ) : !rows.length ? (
          <p className="py-4 text-sm text-muted-foreground">No docs here.</p>
        ) : (
          <ul ref={root} aria-label="Docs" className="m-0 -mx-6 list-none p-0 max-md:-mx-4">
            {rows.map((p) => {
              const Icon = docIcon(p.kind);
              const who = p.created_by_membership_id
                ? people.nameOf(p.created_by_membership_id)
                : null;
              const star = starred(p.id);
              const archived = Boolean(p.archived_at);
              return (
                <li key={p.id} data-list-row="" data-doc-id={p.id}>
                  <ListRow
                    className={archived ? "px-6 opacity-70 max-md:px-4" : "px-6 max-md:px-4"}
                    lead={<Icon aria-hidden="true" />}
                    title={p.title}
                    render={pageLink(p, { "data-row-focus": "" })}
                    chips={
                      <>
                        {star ? (
                          <StarIcon
                            aria-label="Starred"
                            className="size-3.5 fill-current text-warning"
                          />
                        ) : null}
                        {archived ? <Badge variant="neutral">Archived</Badge> : null}
                        {p.upload ? (
                          <DocUploadChip api={api} co={co} pageId={p.id} upload={p.upload} />
                        ) : null}
                        {p.upload ? (
                          <Badge variant="outline">{uploadKind(p.upload.mime)}</Badge>
                        ) : null}
                        {spaceId && p.space_id !== spaceId ? (
                          <SharedMarker home={spaceName(p.space_id)} />
                        ) : null}
                      </>
                    }
                    meta={spaceName(p.space_id)}
                    trailing={
                      <>
                        <span className="flex items-center gap-1.5 max-md:hidden">
                          {who ? (
                            <>
                              <AvatarTile size="xs" name={who.name} kind={who.kind} aria-hidden />
                              <span className="max-w-32 truncate">{who.name}</span>
                            </>
                          ) : (
                            "—"
                          )}
                        </span>
                        <time dateTime={p.updated_at} className="w-24 text-right">
                          edited {ago(p.updated_at)}
                        </time>
                      </>
                    }
                    actions={
                      <span className="flex items-center gap-0.5" data-slot="doc-actions">
                        {/* Phones keep the title: the "…" menu carries every action there. */}
                        <span className="contents max-md:hidden">
                          {action(`Open ${p.title}`, ExternalLinkIcon, { render: pageLink(p) })}
                          {action(star ? `Unstar ${p.title}` : `Star ${p.title}`, StarIcon, {
                            "aria-pressed": star,
                            onClick: () => favorites.toggle({ kind: "page", id: p.id }, p.title),
                          })}
                          {archived
                            ? null
                            : action(`Move ${p.title}`, FolderInputIcon, {
                                onClick: () => setMoving(p),
                              })}
                          {archived
                            ? null
                            : action(`Share ${p.title}`, Share2Icon, {
                                onClick: () => setSharing(p),
                              })}
                          {archived
                            ? action(`Restore ${p.title}`, ArchiveRestoreIcon, {
                                disabled: writes.restore.isPending,
                                onClick: () => writes.restore.mutate(p),
                              })
                            : action(`Archive ${p.title}`, ArchiveIcon, {
                                disabled: writes.archive.isPending,
                                onClick: () => writes.archive.mutate(p),
                              })}
                          {action(`Delete ${p.title}`, Trash2Icon, {
                            onClick: () => setDeleting(p),
                          })}
                        </span>
                        <DropdownMenu>
                          <DropdownMenuTrigger
                            render={
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                aria-label={`More actions for ${p.title}`}
                              />
                            }
                          >
                            <EllipsisIcon aria-hidden="true" />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="min-w-44">
                            <DropdownMenuItem
                              onClick={() =>
                                void navigate({
                                  to: "/c/$slug/work/p/$pageId",
                                  params: { slug: company.slug, pageId: p.id },
                                })
                              }
                            >
                              <ExternalLinkIcon aria-hidden="true" />
                              Open
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => favorites.toggle({ kind: "page", id: p.id }, p.title)}
                            >
                              <StarIcon aria-hidden="true" />
                              {star ? "Unstar" : "Star"}
                            </DropdownMenuItem>
                            {archived ? null : (
                              <DropdownMenuItem onClick={() => setMoving(p)}>
                                <FolderInputIcon aria-hidden="true" />
                                Move
                              </DropdownMenuItem>
                            )}
                            {archived ? null : (
                              <DropdownMenuItem onClick={() => setSharing(p)}>
                                <Share2Icon aria-hidden="true" />
                                Share
                              </DropdownMenuItem>
                            )}
                            {archived ? (
                              <DropdownMenuItem onClick={() => writes.restore.mutate(p)}>
                                <ArchiveRestoreIcon aria-hidden="true" />
                                Restore
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => writes.archive.mutate(p)}>
                                <ArchiveIcon aria-hidden="true" />
                                Archive
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => copyDocLink(company.slug, p.id)}>
                              <LinkIcon aria-hidden="true" />
                              Copy link
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem variant="destructive" onClick={() => setDeleting(p)}>
                              <Trash2Icon aria-hidden="true" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </span>
                    }
                  />
                </li>
              );
            })}
          </ul>
        )}
        <DeletedDocs api={api} company={company} />
      </div>
      <NewDocDialog api={api} company={company} target={newDoc} onClose={() => setNewDoc(null)} />
      <UploadDocDialog
        api={api}
        company={company}
        open={uploading}
        spaceId={spaceId || undefined}
        files={dropped}
        onClose={() => {
          setUploading(false);
          setDropped(undefined);
        }}
      />
      <PageSearchDialog api={api} company={company} open={searching} onOpenChange={setSearching} />
      {moving ? (
        <MovePageDialog
          api={api}
          company={company}
          page={moving}
          spaces={spaces.data?.data ?? []}
          pages={tree.data?.data ?? []}
          open
          onOpenChange={(open) => (open ? null : setMoving(null))}
        />
      ) : null}
      {sharing ? (
        <SharePageDialog
          api={api}
          company={company}
          page={sharing}
          open
          onOpenChange={(open) => (open ? null : setSharing(null))}
        />
      ) : null}
      <DeleteDocDialog
        doc={deleting}
        onCancel={() => setDeleting(null)}
        onConfirm={(d) => {
          writes.remove.mutate(d);
          setDeleting(null);
        }}
      />
    </div>
  );
}

/** "Delete {title}?" — the doc leaves every list; its pages move up a level. */
export function DeleteDocDialog<T extends { id: string; title: string }>({
  doc,
  onCancel,
  onConfirm,
}: {
  doc: T | null;
  onCancel: () => void;
  onConfirm: (doc: T) => void;
}) {
  return (
    <AlertDialog open={doc !== null} onOpenChange={(open) => (open ? null : onCancel())}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {doc?.title}?</AlertDialogTitle>
          <AlertDialogDescription>
            It leaves Docs, the spaces and search, and agents stop reading it. The pages under it
            move up a level.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep it</AlertDialogCancel>
          <AlertDialogAction variant="destructive" onClick={() => (doc ? onConfirm(doc) : null)}>
            Delete doc
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
